import { queryOptions, useQuery } from "@tanstack/react-query";
import { ApiError, api, call } from "./api";

/**
 * Query definitions shared by route loaders and components. Keys are grouped
 * by feature; invalidate with the key prefix (e.g. `["users"]`).
 */
export const queryKeys = {
  setupStatus: ["setup", "status"] as const,
  me: ["me"] as const,
  users: ["users"] as const,
  invitations: ["invitations"] as const,
  invitation: (token: string) => ["invitations", "token", token] as const,
  uploads: ["uploads"] as const,
  uploadCount: ["uploads", "count"] as const,
  resetLink: (token: string) => ["password-reset", token] as const,
};

export const setupStatusQuery = queryOptions({
  queryKey: queryKeys.setupStatus,
  queryFn: () => call(api.setup.status.$get()),
  staleTime: Number.POSITIVE_INFINITY,
});

async function fetchMe() {
  try {
    return await call(api.me.$get());
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export type Me = NonNullable<Awaited<ReturnType<typeof fetchMe>>>;

/** The signed-in user, or `null` when there is no valid session. */
export const meQuery = queryOptions({
  queryKey: queryKeys.me,
  queryFn: fetchMe,
  staleTime: 60_000,
});

export const usersQuery = queryOptions({
  queryKey: queryKeys.users,
  queryFn: () => call(api.users.$get()),
});

export const invitationsQuery = queryOptions({
  queryKey: queryKeys.invitations,
  queryFn: () => call(api.invitations.$get()),
});

/** Public invitation check; resolves to `null` for invalid/expired/used links. */
export const invitationQuery = (token: string) =>
  queryOptions({
    queryKey: queryKeys.invitation(token),
    queryFn: async () => {
      try {
        return await call(api.invitations[":token"].$get({ param: { token } }));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    retry: false,
  });

/** Public reset link check; resolves to `null` for invalid/expired/used links. */
export const resetLinkQuery = (token: string) =>
  queryOptions({
    queryKey: queryKeys.resetLink(token),
    queryFn: async () => {
      try {
        return await call(api["password-reset"][":token"].$get({ param: { token } }));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    retry: false,
  });

/** Pending uploads waiting in the inbox (own for editors, all for admins). */
export const pendingUploadsQuery = queryOptions({
  queryKey: [...queryKeys.uploads, "pending"] as const,
  queryFn: () => call(api.uploads.$get()),
});

/** Number of visible pending uploads, shown as a badge in the navigation. */
export const uploadCountQuery = queryOptions({
  queryKey: queryKeys.uploadCount,
  queryFn: () => call(api.uploads.count.$get()),
  refetchOnWindowFocus: true,
  refetchInterval: 120_000,
});

/** Pending upload count for the navigation badges; `0` for users who cannot see the inbox. */
export function usePendingUploadCount(enabled: boolean): number {
  return useQuery({ ...uploadCountQuery, enabled }).data?.count ?? 0;
}
